import { nanoid } from 'nanoid';

export const getRandomId = (size = 10) => nanoid(size);

const buildGridTemplates = (value: string, dimensionValue: number) => {
  let templates = [];
  let current = '';
  let inParens = 0;
  for (let i = 0; i < value?.length; i++) {
    const char = value[i];
    if (char === '(') inParens++;
    if (char === ')') inParens--;
    if (char === ' ' && inParens === 0) {
      if (current.trim() !== '') {
        templates.push(current.trim());
        current = '';
      }
    } else {
      current += char;
    }
  }
  if (current.trim() !== '') {
    templates.push(current.trim());
  }

  if (templates.length === 0 && dimensionValue > 0) {
    // if no templates defined, create default ones based on dimensionValue
    templates = Array.from({ length: dimensionValue }, () => '1fr');
  } else if (templates.length > dimensionValue) {
    // if more templates than dimensionValue, trim the excess
    templates = templates.slice(0, dimensionValue);
  } else if (templates.length < dimensionValue) {
    // if fewer templates than dimensionValue, add '1fr' to make up the difference
    templates = [...templates, ...Array.from({ length: dimensionValue - templates.length }, () => '1fr')];
  }

  return templates.join(' ').trim();
};

const properStyleText = (styleText: string) => {
  let properText = styleText;
  if (properText && properText[properText.length - 1] !== ';') {
    properText += ';';
  }
  return properText;
};

const cache: any = {};
const DEBUG = false;

export const buildLayoutBuilder = (rootNode: string, layout: any, layouts: any, elements: any) => {
  if (cache[`${rootNode}-${layout}`]) {
    DEBUG && console.log('Returning cached result for:', `${rootNode}-${layout}`);
    return cache[`${rootNode}-${layout}`];
  }

  const result = {
    success: false,
    data: { styleText: '', className: '', content: null },
  };

  let elementsMap: any = {};

  Object.keys(elements).forEach((key) => {
    if (key == rootNode) {
      const element = elements[key];
      element.type = 'Container';
      elementsMap = { ...element.linkedNodes };
      element.nodes = [];
      delete element.linkedNodes;
      delete elements.parent;

      if (rootNode !== 'ROOT') {
        elements['ROOT'] = element;
      }
    }
  });

  if (layout && layouts && Array.isArray(layouts)) {
    const found = layouts.find((l) => l.id === layout);
    if (found) {
      let data: any = {};
      try {
        // Replace placeholders in the layout with actual element IDs
        const parsedLayout = JSON.parse(found.data);
        data = parsedLayout;
      } catch (e) {
        console.error('Error parsing layout JSON:', e);
        return result;
      }

      let styleText = '';
      const className = 'lb-' + getRandomId();

      const parse = (nodeId: string, parentNodeId: string | void) => {
        if (nodeId === null || nodeId === '') return;
        if (!data?.[nodeId]) return;

        const node = data[nodeId];

        if (node.type === 'grid') {
          styleText += `\n${nodeId === 'ROOT' ? `&.${className}` : `.lb-${node.elementId}`} {
            display: grid;
            grid-template-columns: ${buildGridTemplates(node?.gridTemplateColumns, node?.cols)};
            grid-template-rows: ${buildGridTemplates(node?.gridTemplateRows, node?.rows)};
            ${node?.gap && node?.gap !== '0' ? `gap: ${node?.gap ?? '0'};` : ''}
            ${node?.padding && node?.padding !== '0' ? `padding: ${node?.padding ?? '0'};` : ''}
            align-content: ${node?.ac ?? 'normal'};
            justify-content: ${node?.jc ?? 'normal'};
            align-items: ${node?.ai ?? 'normal'};
            justify-items: ${node?.ji ?? 'normal'};
            position: relative;
            ${nodeId !== 'ROOT' ? `grid-area: ${node.y + 1} / ${node.x + 1} / span ${node.h} / span ${node.w};` : ''}
            ${node?.st ? properStyleText(node?.st) : ''}
          }`;
        } else if (node.type === 'flex') {
          styleText += `\n${nodeId === 'ROOT' ? `&.${className}` : `.lb-${node.elementId}`} {
            display: flex;
            flex-direction: ${node?.fd ?? 'row'};
            flex-wrap: ${node?.fw ?? 'nowrap'};
            align-content: ${node?.ac ?? 'normal'};
            justify-content: ${node?.jc ?? 'normal'};
            align-items: ${node?.ai ?? 'normal'};
            ${node?.gap && node?.gap !== '0' ? `gap: ${node?.gap ?? '0'};` : ''}
            ${node?.padding && node?.padding !== '0' ? `padding: ${node?.padding ?? '0'};` : ''}
            position: relative;
            ${nodeId !== 'ROOT' ? `grid-area: ${node.y + 1} / ${node.x + 1} / span ${node.h} / span ${node.w};` : ''}
            ${node?.st ? properStyleText(node?.st) : ''}
          }`;
        } else {
          styleText += `\n.lb-${node.elementId} {
            position: relative;
            grid-area: ${node.y + 1} / ${node.x + 1} / span ${node.h} / span ${node.w};
            ${node.lines && node.lines !== '0' ? `--clamp-lines: ${node.lines};` : ''}
            ${node?.st ? properStyleText(node?.st) : ''}            
          }`;
        }

        if (node.layout && Array.isArray(node.layout)) {
          node.layout.forEach((childId: any) => {
            const childNode = elementsMap[data[childId]?.elementId];
            const parentNode = elementsMap[data[nodeId]?.elementId] ?? 'ROOT';
            if (!elements[parentNode].nodes) elements[parentNode].nodes = [];
            elements[parentNode].nodes.push(childNode);
          });

          node.layout.map((childId: string) => parse(childId, nodeId));

          if (nodeId === 'ROOT') return;
        }

        const craftNodeId = elementsMap[node.elementId];
        if (elements[craftNodeId]) {
          const cls = elements[craftNodeId].props.className;
          if (cls) {
            if (
              !cls
                .split(' ')
                .map((e: string) => e?.trim?.())
                .includes(`lb-${node.elementId}`)
            ) {
              elements[craftNodeId].props.className = cls + ' ' + `lb-${node.elementId}` + ' ' + (node.cn ?? '');
            } else {
              elements[craftNodeId].props.className = cls + ' ' + (node.cn ?? '');
            }
          } else {
            elements[craftNodeId].props.className = `lb-${node.elementId} ${node.cn ?? ''}`;
          }
          return;
        }
      };

      parse('ROOT');

      const cls = elements['ROOT'].props.className;
      if (cls) {
        if (
          !cls
            .split(' ')
            .map((e: string) => e?.trim?.())
            .includes(className)
        ) {
          elements['ROOT'].props.className = cls + ' ' + className;
        }
      } else {
        elements['ROOT'].props.className = className;
      }

      const st = elements['ROOT'].props.styleText;
      if (st) {
        if (st[st.length - 1] !== ';') {
          elements['ROOT'].props.styleText = st + ';\n' + styleText;
        } else {
          elements['ROOT'].props.styleText = st + '\n' + styleText;
        }
      } else {
        elements['ROOT'].props.styleText = styleText;
      }

      result.success = true;
      result.data = { styleText, className, content: elements };

      cache[`${rootNode}-${layout}`] = result;
      DEBUG && console.log('Returning processed result for:', `${rootNode}-${layout}`);
      return result;
    }
  }

  return result;
};
